/**
 * WriteAccessProvider.tsx — the providers for lib/writeAccess (the mapping,
 * the context and the hook live there; this file exports components only).
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { api } from '../api/client'
import { WriteAccessContext, writeAccessFor } from './writeAccess'

export function WriteAccessProvider({ role, children }: {
  role: string | null | undefined
  children: ReactNode
}) {
  const value = useMemo(() => writeAccessFor(role), [role])
  return <WriteAccessContext.Provider value={value}>{children}</WriteAccessContext.Provider>
}
/**
 * For shells that do not already hold a /v1/me read (Mission Control): one
 * GET /v1/me per tenant, then the provider. Until it resolves — and on any
 * error — the role is null, so every write control stays hidden.
 */
export function SessionWriteAccess({ tenantKey, children }: {
  /** Re-read /me when the active tenant changes (the role is per tenant). */
  tenantKey: string
  children: ReactNode
}) {
  const [role, setRole] = useState<{ key: string; role: string | null } | null>(null)
  useEffect(() => {
    let live = true
    api.get<{ role?: string }>('/v1/me')
      .then(me => { if (live) setRole({ key: tenantKey, role: typeof me?.role === 'string' ? me.role : null }) })
      .catch(() => { if (live) setRole({ key: tenantKey, role: null }) })
    return () => { live = false }
  }, [tenantKey])
  // A role read for a different tenant is never applied to this one.
  const current = role && role.key === tenantKey ? role.role : null
  return <WriteAccessProvider role={current}>{children}</WriteAccessProvider>
}
